import React, { useEffect, useState } from "react";
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Drawer,
    Upload,
    message,
    Spin,
} from "antd";
import { CloseOutlined, InboxOutlined } from "@ant-design/icons";
import moment from "moment";
import { 
    useGetApprovalRequestsOptionsQuery, 
    useGetApprovalRequestByIdQuery,
    useCreateApprovalRequestMutation,
    useUpdateApprovalRequestMutation 
} from "../../appRedux/api/approvalRequestsApi";
import "./ApprovalRequestAddEditDrawer.css";

const { TextArea } = Input;
const { Dragger } = Upload;
const { Option } = Select;

const formItemLayout = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 5 },
    },
    wrapperCol: {
        xs: { span: 24 },
        sm: { span: 18 },
    },
};

// Constants for validation
const MAX_TOTAL_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes

const ApprovalRequestAddEditDrawer = ({ 
    visible, 
    onClose, 
    editMode, 
    requestId, // Changed from requestData to requestId
    onSuccess
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [removedFiles, setRemovedFiles] = useState([]);
    const [initialFiles, setInitialFiles] = useState([]);
    const [form] = Form.useForm();

    // API mutations for create and update
    const [createApprovalRequest] = useCreateApprovalRequestMutation();
    const [updateApprovalRequest] = useUpdateApprovalRequestMutation();

    // Fetch options data
    const { data: optionsData, isLoading: optionsLoading } = useGetApprovalRequestsOptionsQuery(undefined, {
        skip: !visible, // Only fetch when drawer is visible
    });

    // Fetch approval request data for edit mode using requestId
    const { data: approvalRequestData, isLoading: requestLoading } = useGetApprovalRequestByIdQuery(requestId, {
        skip: !editMode || !requestId || !visible, // Only fetch in edit mode when ID is available and drawer is visible
    });

    // Extract options from API response
    const requestTypeOptions = optionsData?.approval_request_type_options || [];
    const licenseOptions = optionsData?.licenses || [];

    useEffect(() => {
        if (editMode && approvalRequestData && visible) {
            // Set initial files if editing existing request with documents
            const initialFiles = approvalRequestData?.documents?.map((document, index) => ({
                uid: document.document_id || `-${index}`,
                name: document.file?.split('/').pop() || `Document ${index + 1}`,
                status: "done",
                url: document.file || "#",
                documentId: document.document_id, // Store document_id for deletion
            })) || [];
            
            setInitialFiles(initialFiles);
            
            // Set form values for edit mode
            form.setFieldsValue({
                requestType: approvalRequestData.approval_request_type,
                license: approvalRequestData.license?.license_id || approvalRequestData.license?.name,
                partyName: approvalRequestData.party_name,
                furtherDetails: approvalRequestData.request_details,
                attachments: initialFiles,
            });
        } else if (!editMode && visible) {
            // Reset form for add mode
            form.resetFields();
            setRemovedFiles([]);
            setInitialFiles([]);
        }
    }, [editMode, approvalRequestData, visible, form]);

    const handleRemove = (file) => {
        if (file.documentId) {
            setRemovedFiles(prev => [...prev, file.documentId]);
        }
        return true;
    };

    const normFile = (e) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e && e.fileList;
    };

    // Function to calculate total size of files
    const calculateTotalSize = (fileList) => {
        return fileList.reduce((total, file) => {
            if (file.originFileObj) {
                return total + file.originFileObj.size;
            }
            // For existing files, we don't have size info, so we assume they're within limits
            return total;
        }, 0);
    };

    // Function to count new files (files with originFileObj)
    const countNewFiles = (fileList) => {
        return fileList.filter(file => file.originFileObj).length;
    };

    const checkFile = (file, fileList) => {
        const allowedFileTypes = [
            "application/pdf", 
            "application/msword", 
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "image/jpeg", 
            "image/jpg", 
            "image/png"
        ];
        
        const isTypeValid = allowedFileTypes.includes(file.type);

        if (!isTypeValid) {
            message.error("Please upload a PDF, Word, JPEG, or PNG document!");
            return false;
        }

        // Check total size
        const currentTotalSize = calculateTotalSize(fileList);
        const newTotalSize = currentTotalSize + file.size;
        
        if (newTotalSize > MAX_TOTAL_FILE_SIZE) {
            message.error(`Total files size must be less than or equal to 20MB! Current total: ${(currentTotalSize / 1024 / 1024).toFixed(2)}MB, New file: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
            return false;
        }

        return true;
    };

    const onFinish = async (values) => {
        setIsSubmitting(true);
        
        try {
            // Final validation before submission
            const fileList = values.attachments || [];
            const totalSize = calculateTotalSize(fileList);
            const newFileCount = countNewFiles(fileList);

            // For new requests, ensure at least one file is uploaded
            if (!editMode && newFileCount === 0) {
                message.error("Please upload at least one attachment");
                setIsSubmitting(false);
                return;
            }

            // Check total size limit
            if (totalSize > MAX_TOTAL_FILE_SIZE) {
                message.error(`Total files size must be less than or equal to 20MB! Current total: ${(totalSize / 1024 / 1024).toFixed(2)}MB`);
                setIsSubmitting(false);
                return;
            }

            if (editMode) {
                // Edit existing request - use FormData for PUT to handle file updates
                const formData = new FormData();
                
                // Append basic fields
                formData.append('approval_request_type', values.requestType);
                formData.append('license', values.license);
                formData.append('party_name', values.partyName);
                formData.append('request_details', values.furtherDetails || '');

                // Handle file deletions - send comma-separated document IDs
                if (removedFiles.length > 0) {
                    formData.append('files_to_delete', removedFiles.join(','));
                }

                // Handle new file uploads for edit mode
                const newFiles = values.attachments?.filter(file => file.originFileObj) || [];
                newFiles.forEach(file => {
                    formData.append('file', file.originFileObj);
                });

                // Update request using the requestId with FormData
                await updateApprovalRequest({
                    id: requestId,
                    data: formData
                }).unwrap();
                
                // message.success("Request updated successfully!");
            } else {
                // Create new request with FormData
                const formData = new FormData();
                
                // Append basic fields
                formData.append('approval_request_type', values.requestType);
                formData.append('license', values.license);
                formData.append('party_name', values.partyName);
                formData.append('request_details', values.furtherDetails || '');

                // Handle file uploads for new request
                const newFiles = values.attachments?.filter(file => file.originFileObj) || [];
                newFiles.forEach(file => {
                    formData.append('file', file.originFileObj);
                });

                await createApprovalRequest(formData).unwrap();
                // message.success("Request submitted successfully!");
            }
            
            onSuccess();
            handleClose();
            
        } catch (error) {
            console.error('Error submitting request:', error);
            message.error(
                error?.data?.message || 
                `Failed to ${editMode ? 'update' : 'submit'} request. Please try again.`
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        form.resetFields();
        setRemovedFiles([]);
        setInitialFiles([]);
        setIsSubmitting(false);
        onClose();
    };

    const isLoading = optionsLoading || (editMode && requestLoading);

    return (
        <Drawer
            className="approval-request-add-edit-drawer"
            title={null}
            placement="right"
            width={800}
            onClose={isSubmitting ? () => {} : handleClose}
            open={visible}
            closable={false}
            footer={
                <div className="drawer-footer" style={{ textAlign: 'right' }}>
                    <Button 
                        onClick={handleClose} 
                        style={{ marginRight: 8 }} 
                        disabled={isSubmitting || isLoading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={() => form.submit()} 
                        disabled={isSubmitting || isLoading}
                        loading={isSubmitting}
                    >
                        {editMode ? 'Update Request' : 'Submit Request'}
                    </Button>
                </div>
            }
        >
            {(isSubmitting || isLoading) && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <Spin size="large" />
                </div>
            )}
            
            <div className="drawer-header">
                <span>{editMode ? "Edit Approval Request" : "Raise Approval Request"}</span>
                <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={handleClose}
                    disabled={isSubmitting || isLoading}
                />
            </div>

            <Form
                {...formItemLayout}
                form={form}
                name="approval-request-form"
                onFinish={onFinish}
                disabled={isSubmitting || isLoading}
                scrollToFirstError
            >
                <Form.Item
                    name="requestType"
                    label="Type of Request"
                    rules={[
                        { required: true, message: "Please select the type of request" },
                    ]}
                >
                    <Select placeholder="Select request type" loading={optionsLoading}>
                        {requestTypeOptions.map((type) => (
                            <Option key={type.value || ""} value={type.value || ""}>
                                {type.label || type}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="license"
                    label="License"
                    rules={[
                        { required: true, message: "Please select a license" },
                    ]}
                >
                    <Select placeholder="Select license" loading={optionsLoading}>
                        {licenseOptions.map((license) => (
                            <Option key={license.value || license} value={license.value || license}>
                                {license.label || license}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="partyName"
                    label="Name of the Party"
                    rules={[
                        { required: true, message: "Please enter the party name" },
                        { max: 255, message: "Party name must be less than 255 characters" }
                    ]}
                >
                    <Input placeholder="Enter party name" />
                </Form.Item>

                <Form.Item
                    name="furtherDetails"
                    label="Further Details of Request"
                    rules={[
                        { max: 1000, message: "Details must be less than 1000 characters" }
                    ]}
                >
                    <TextArea 
                        rows={4} 
                        placeholder="Enter additional details about the request" 
                    />
                </Form.Item>

                <Form.Item
                    name="attachments"
                    label="Attachments"
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    rules={[
                        { 
                            required: !editMode, 
                            // message: "Please upload at least one attachment"
                        },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                const fileList = value || [];
                                
                                // In edit mode, attachments are optional (can keep existing ones)
                                if (editMode && fileList.length === 0) {
                                    return Promise.resolve();
                                }
                                
                                // In add mode, at least one attachment is required
                                if (!editMode && fileList.length === 0) {
                                    return Promise.reject(new Error("Please upload at least one attachment"));
                                }

                                // Check each file and calculate total size
                                let totalSize = 0;
                                for (const file of fileList) {
                                    // Only validate new files (those with originFileObj)
                                    if (file.originFileObj) {
                                        const allowedFileTypes = [
                                            "application/pdf", 
                                            "application/msword", 
                                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                                            "image/jpeg", 
                                            "image/jpg", 
                                            "image/png"
                                        ];
                                        
                                        if (!allowedFileTypes.includes(file.originFileObj.type)) {
                                            return Promise.reject(
                                                new Error(`Invalid file type for ${file.name}. Please upload PDF, Word, JPEG, or PNG documents only!`)
                                            );
                                        }

                                        totalSize += file.originFileObj.size;
                                    }
                                }

                                // Check total size
                                if (totalSize > MAX_TOTAL_FILE_SIZE) {
                                    return Promise.reject(
                                        new Error(`Total files size must be less than or equal to 20MB! Current total: ${(totalSize / 1024 / 1024).toFixed(2)}MB`)
                                    );
                                }

                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <Dragger
                        name="attachments"
                        multiple={true}
                        beforeUpload={(file, fileList) => {
                            return checkFile(file, fileList);
                        }}
                        accept=".pdf,.doc,.docx,.png,.jpeg,.jpg"
                        onRemove={handleRemove}
                        defaultFileList={editMode ? initialFiles : []}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                            Click or drag files to this area to upload
                        </p>
                        <p className="ant-upload-hint">
                            Support for multiple uploads (PDF, Word, JPEG, PNG). 
                            Max total size: 20MB.
                            {editMode && " Removing files will delete them from the request."}
                        </p>
                    </Dragger>
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default ApprovalRequestAddEditDrawer;